import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaTimes, FaReply } from 'react-icons/fa';
import { useSwipeable } from 'react-swipeable';
import api from '../../api/axiosConfig';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../utils/avatarColor';

const countReplies = (comment) => {
  if (!comment.replies || comment.replies.length === 0) return 0;
  return comment.replies.reduce(
    (total, reply) => total + 1 + countReplies(reply),
    0
  );
};

const CommentItem = ({ comment, onReplyPosted, depth = 0, activeReplyId, setActiveReplyId}) => {
  const { replies } = comment;
  const replyCount = useMemo(() => countReplies({ replies }), [replies]);
  const isReplyFormOpen = activeReplyId === comment.id;
  const { t } = useTranslation('CommentPanel');
  
  
  const toggleReplyForm = () => {
    if (isReplyFormOpen) {
      setActiveReplyId(null);
    } else {
      setActiveReplyId(comment.id);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    const text = e.target.replyText.value.trim();
    if (!text) return;
    try {
      const res = await api.post(`/properties/${comment.property}/comments/`, {
        text,
        parent: comment.id
      });
      onReplyPosted(res.data, comment.id);
      setActiveReplyId(null); 
    } catch (err) {
      alert(t('comments.errors.replyFailed'));
    }
  };

  const maxDepth = 3;
  const isNested = depth > 0;
  const indentClass = isNested ? `ml-${Math.min(depth * 4, 12)} border-l-2 border-gray-700 pl-3` : '';

  return (
    <div className={indentClass}>
      <div className="flex items-start gap-3">
        {(() => {
          const hasAvatar = comment.user?.avatar && String(comment.user.avatar).startsWith('http');
          if (hasAvatar) {
            return (
              <img
                src={comment.user.avatar}
                alt={comment.user?.username || t('comments.user.fallback')}
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                onError={(e) => {
                  e.target.src = 'https://ui-avatars.com/api/?name=Anonymous&background=ccc&color=fff';
                }}
              />
            );
          }

          const displayName = comment.user?.username || t('comments.user.anonymous');
          const initials = displayName.charAt(0).toUpperCase();
          const bgColor = stringToColor(comment.user?.username || comment.user?.id || 'anonymous').substring(1);
          const fallbackUrl = 'https://ui-avatars.com/api/?name=Anonymous&background=ccc&color=fff';

          return (
            <img
              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff`}
              alt={comment.user?.username || t('comments.user.fallback')}
              className="w-8 h-8 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.target.src = fallbackUrl;
              }}
            />
          );
        })()}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white">{comment.user?.username}</span>
            <span className="text-xs text-gray-400">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>
          <p className="mt-1 text-[var(--text-black)] leading-relaxed break-words">{comment.text}</p>

          <div className="flex items-center mt-2 gap-3">
            {depth < maxDepth && (
              <button
                onClick={toggleReplyForm}
                className="flex items-center gap-1 text-[var(--text-black)] bg-[var(--bg-black)] border-none hover:text-pink-400 text-sm transition-colors"
              >
                <FaReply size={12} />
                <span>{t('comments.form.reply.button')}</span>
                {replyCount > 0 && (
                  <span className="ml-1 text-xs text-gray-500">{t('comments.form.reply.count', { count: replyCount })}</span>
                )}
              </button>
            )}
          </div>

          {/* Reply Form */}
          {isReplyFormOpen && (
            <form onSubmit={handleReplySubmit} className="mt-3 flex gap-2">
              <input
                name="replyText"
                placeholder={t('comments.form.reply.placeholder')}
                className="flex-1 p-2 text-sm rounded bg-[var(--bg-black)] text-[var(--text-black)] placeholder-gray-400 border border-[var(--border)] focus:outline-none focus:ring-1 focus:ring-pink-500"
              />
              <button
                type="submit"
                className="px-3 py-1 text-sm rounded round-lg font-medium bg-pink-500 hover:bg-pink-600 text-[var(--text-black)] border-none"
              >
                {t('comments.form.reply.submit')}
              </button>
            </form>
          )}

          {/* Nested Replies */}
          {comment.replies?.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onReplyPosted={onReplyPosted}
              depth={depth + 1}
              activeReplyId={activeReplyId}
              setActiveReplyId={setActiveReplyId}
              className="text-[var(--text-black)]"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Panel
const CommentPanel = ({ isOpen, onClose, propertyId, comments = [], onCommentPosted }) => {
  const [newComment, setNewComment] = useState('');
  const [activeReplyId, setActiveReplyId] = useState(null);
  const { t } = useTranslation('CommentPanel');
  const panelRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const res = await api.post(`/properties/${propertyId}/comments/`, {
        text: newComment
      });
      onCommentPosted?.(res.data);
      setNewComment('');
    } catch (err) {
      console.error('Comment error:', err);
      alert(t('comments.errors.postFailed'));
    }
  };

  const handlers = useSwipeable({
    onSwipedRight: () => onClose(),
    trackMouse: true
  });

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Reset reply form when panel closes
  useEffect(() => {
    if (!isOpen) setActiveReplyId(null);
  }, [isOpen]);

  const handleReplyPosted = (newReply) => {
    onCommentPosted?.(newReply);
  };

  if (!isOpen) return null;

  // Only top-level comments (no parent)
  const topLevelComments = comments.filter(comment => !comment.parent);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        {...handlers}
        className="w-full max-w-sm h-full overflow-y-auto p-4 pt-0 pb-10 relative bg-[var(--bg-black)] text-[var(--text-black)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3 sticky top-0 z-10 pb-3 border-b border-gray-200 dark:border-gray-700 bg-[var(--bg-black)]">
          <h3 className="text-xl font-bold">{t('comments.title', { count: topLevelComments.length })}</h3>
          <button onClick={onClose} className="p-2 rounded-full bg-[var(--bg-black)] text-pink-500 hover:text-pink-700 border-none">
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }} className="mb-5 flex gap-2">
          <input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={t('comments.form.placeholder')}
            className="flex-1 p-3 rounded-lg bg-[var(--bg-black)] text-[var(--text-black)] border border-[var(--border)] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500"
            autoFocus
          />
          <button
            type="submit"
            disabled={!newComment.trim()}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              newComment.trim()
                ? 'bg-pink-500 hover:bg-pink-600 text-white border-none'
                : 'bg-[var(--bg-black)] text-[var(--text-black)] cursor-not-allowed border border-[var(--border)]'
            }`}
          >
            {t('comments.form.submit')}
          </button>
        </form>

        <div className="space-y-4">
          {topLevelComments.length > 0 ? (
            topLevelComments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onReplyPosted={handleReplyPosted}
                depth={0}
                activeReplyId={activeReplyId}
                setActiveReplyId={setActiveReplyId}
              />
            ))
          ) : (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              {t('comments.empty')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentPanel;